import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Filter, Loader2, Search } from 'lucide-react';
import { useTransactions, useTransactionsPaginated, useCreateTransaction, useUpdateTransaction, useDeleteTransaction, Transaction } from '@/hooks/useTransactions';
import { useFetchAccounts } from '@accountHooks';
import { useFetchCategories, useSeedDefaultCategories } from '@metadataHooks';
import { useCreateTransfer } from '@/hooks/useCreateTransfer';
import { TransactionForm, TransactionFormData, TransferFormData } from '@/components/transaction-form/TransactionForm';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { TransactionListItem } from '@/components/transaction-list-item/TransactionListItem';
import { TimePeriodSelector, getInitialTimePeriod, TimePeriod } from '@/components/time-period-selector/TimePeriodSelector';
import { TransactionDistributionChart } from '@/components/transaction-distribution-chart/TransactionDistributionChart';
import { ChartErrorBoundary } from '@/components/chart-error-boundary/ChartErrorBoundary';
import { TransactionMobileCard } from '@/components/transaction-mobile-card/TransactionMobileCard';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';

export default function Transactions() {
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState<string>('');
  const [filterAccount, setFilterAccount] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(() => getInitialTimePeriod());
  const [pageSize, setPageSize] = useState<number>(50);

  const filters = {
    type: filterType || undefined,
    accountId: filterAccount || undefined,
    categoryId: filterCategory || undefined,
    dateFrom: filterDateFrom || undefined,
    dateTo: filterDateTo || undefined,
    searchQuery: searchQuery || undefined,
  };

  // Use regular query for initial count check
  const { data: allTransactions } = useTransactions(filters);
  const shouldPaginate = (allTransactions?.length || 0) > 100;

  // Use paginated query when needed
  const {
    data: paginatedData,
    isLoading: isPaginatedLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTransactionsPaginated(filters, pageSize);

  // Use regular query for small datasets
  const { data: regularTransactions, isLoading: isRegularLoading } = useTransactions(filters);

  // Determine which data to use
  const transactions = shouldPaginate
    ? paginatedData?.pages.flatMap((page) => page.transactions) || []
    : regularTransactions || [];
  const isLoading = shouldPaginate ? isPaginatedLoading : isRegularLoading;
  const { data: accounts } = useFetchAccounts();
  const { data: categories } = useFetchCategories();
  const seedCategories = useSeedDefaultCategories();
  const createTx = useCreateTransaction();
  const updateTx = useUpdateTransaction();
  const deleteTx = useDeleteTransaction();
  const createTransfer = useCreateTransfer();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | undefined>();

  // Extract unique currencies from user's accounts
  const availableCurrencies = useMemo(() => {
    if (!accounts || accounts.length === 0) return ['USD'];
    const currencies = [...new Set(accounts.map(account => account.currency))];
    return currencies.length > 0 ? currencies : ['USD'];
  }, [accounts]);

  // Set initial selected currency to first available currency
  useEffect(() => {
    if (availableCurrencies.length > 0 && !selectedCurrency) {
      setSelectedCurrency(availableCurrencies[0]);
    }
  }, [availableCurrencies, selectedCurrency]);

  // Seed default categories if none exist
  useEffect(() => {
    if (categories && categories.length === 0) {
      seedCategories.mutate();
    }
  }, [categories]);

  const handleSave = async (data: TransactionFormData) => {
    try {
      if (editing) {
        await updateTx.mutateAsync({ id: editing.id, ...data, sub_category_id: data.sub_category_id });
        toast.success('Transaction updated');
      } else {
        await createTx.mutateAsync({ ...data, sub_category_id: data.sub_category_id });
        toast.success('Transaction added');
      }
      setDialogOpen(false);
      setEditing(undefined);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleTransferSave = async (data: TransferFormData) => {
    try {
      const result = await createTransfer.mutateAsync({
        sourceAccountId: data.sourceAccountId,
        targetAccountId: data.targetAccountId,
        amount: data.amount,
        fee: data.fee,
        currency: data.currency,
        transactionDate: data.transactionDate,
        description: data.description,
        notes: data.notes,
      });
      toast.success(`Transfer created successfully (IDs: ${result.source_transaction_id.slice(0, 8)}..., ${result.target_transaction_id.slice(0, 8)}...)`);
      setDialogOpen(false);
      setEditing(undefined);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create transfer');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTx.mutateAsync(id);
      toast.success('Transaction deleted');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const isMobile = useIsMobile();

  const openEdit = (tx: Transaction) => { setEditing(tx); setDialogOpen(true); };
  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const clearFilters = () => { setFilterType(''); setFilterAccount(''); setFilterCategory(''); setFilterDateFrom(''); setFilterDateTo(''); setSearchQuery(''); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Transactions</h1>
          <p className="text-muted-foreground">View and manage your income and expenses.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search transactions..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="mr-2 h-4 w-4" /> Filters
          </Button>
          <Dialog open={dialogOpen} onOpenChange={v => { setDialogOpen(v); if (!v) setEditing(undefined); }}>
            <DialogTrigger asChild>
              <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" /> Add Transaction</Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] sm:w-full sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? 'Edit Transaction' : 'New Transaction'}</DialogTitle>
              </DialogHeader>
              <TransactionForm
                transaction={editing}
                onSave={handleSave}
                onTransferSave={handleTransferSave}
                onCancel={() => setDialogOpen(false)}
                accounts={accounts ?? []}
                categories={categories ?? []}
                defaultCurrency="USD"
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-5">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterAccount} onValueChange={setFilterAccount}>
                <SelectTrigger><SelectValue placeholder="All accounts" /></SelectTrigger>
                <SelectContent>
                  {(accounts ?? []).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  {(categories ?? []).map(c => <SelectItem key={c.id} value={c.id}>{c.icon} {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} placeholder="From" />
              <div className="flex gap-2">
                <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} placeholder="To" />
                <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Distribution Charts Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Distribution Overview</h2>
          <TimePeriodSelector value={timePeriod} onChange={setTimePeriod} />
        </div>
        
        <div className="grid gap-6 md:grid-cols-2 grid-cols-1">
          <ChartErrorBoundary fallbackTitle="Income Distribution">
            <TransactionDistributionChart
              timePeriod={timePeriod}
              chartType="pie"
              transactionType="income"
              currency={selectedCurrency}
            />
          </ChartErrorBoundary>
          <ChartErrorBoundary fallbackTitle="Expense Distribution">
            <TransactionDistributionChart
              timePeriod={timePeriod}
              chartType="pie"
              transactionType="expense"
              currency={selectedCurrency}
            />
          </ChartErrorBoundary>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Transactions</CardTitle>
            <div className="flex items-center gap-4">
              {shouldPaginate && (
                <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Page size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                    <SelectItem value="200">200 per page</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {transactions && transactions.length > 0 && (
                <CurrencyToggle
                  baseCurrency={selectedCurrency}
                  availableCurrencies={availableCurrencies}
                  selectedCurrency={selectedCurrency}
                  onCurrencyChange={setSelectedCurrency}
                  showBalance={false}
                />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : !transactions?.length ? (
            <p className="text-sm text-muted-foreground">
              {!accounts?.length ? 'Create an account first, then add transactions.' : 'No transactions yet. Add your first transaction.'}
            </p>
          ) : (
            <>
              {isMobile ? (
                <div className="space-y-3">
                  {transactions.map(tx => (
                    <TransactionMobileCard
                      key={tx.id}
                      transaction={tx}
                      displayCurrency={selectedCurrency}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onClick={(t) => navigate('/transactions/' + t.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="w-[100px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map(tx => (
                        <TransactionListItem
                          key={tx.id}
                          transaction={tx}
                          displayCurrency={selectedCurrency}
                          onEdit={openEdit}
                          onDelete={handleDelete}
                          onClick={(t) => navigate('/transactions/' + t.id)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {shouldPaginate && hasNextPage && (
                <div className="flex justify-center mt-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading more...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
